/**
 * Pure mapping helpers for DistributorOrderDetail.vue.
 *
 * Extracted so they can be unit-tested against the REAL jsonapi-vuex-normalized
 * order shape (not a mount fixture). The page fetches the order via
 * `store.dispatch("jv/get", ...)`, which denormalizes the JSON:API envelope
 * (data + included[]) into a flat object with a `_jv` key; these helpers project
 * that flat object into the CLIENT-SAFE shape `OrderDetailView` renders.
 *
 * Client-safe by construction: the projection whitelists only client-facing
 * keys (order #, status, date, shopper, store, department, line items, total,
 * shipping). No cost / margin / fee / app_fee / tf_cut key is ever read or
 * forwarded -- the distributor-internal decomposition lives only in
 * OrderProfitBreakdown.
 *
 * Shipping and batch come from a SECOND read,
 * GET /api/reporting/distributor-orders/:id/fulfillment, not from the jv
 * order: SAFRS carries neither, and the endpoint serves the same frozen
 * ship-to the store's Batches page shows, so the two cannot disagree.
 */
import { normalizeBatch } from "@/services/batchManagementService";

/**
 * Convert a dollars value (number, or JSON:API numeric string) to integer
 * cents. Returns null for missing / unparseable values so OrderDetailView
 * hides the amount gracefully.
 *
 * @param {number|string|null|undefined} value
 * @returns {number|null}
 */
export function dollarsToCents(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/**
 * Derive a shopper display name from an order's checkout_fields JSON.
 *
 * Mirrors the backend `_shopper_name` (api/src/endpoints/distributor_orders.py):
 * "First Name" + "Last Name" space-joined, tolerating a missing half. The
 * shopper name is NOT on `account` -- it is captured at checkout and stored in
 * checkout_fields, so this is the authoritative source for the label.
 *
 * @param {object|null|undefined} checkoutFields
 * @returns {string|null} the joined name, or null when neither part is present
 */
export function shopperNameFromCheckoutFields(checkoutFields) {
  if (!checkoutFields || typeof checkoutFields !== "object") return null;
  const first = `${checkoutFields["First Name"] || ""}`.trim();
  const last = `${checkoutFields["Last Name"] || ""}`.trim();
  const joined = [first, last].filter((p) => p.length > 0).join(" ");
  return joined.length > 0 ? joined : null;
}

/**
 * Project a line item (jv-normalized OrderItem, with campaign_product included)
 * into the client-safe item shape OrderDetailView renders.
 *
 * @param {object} item
 * @returns {{quantity: number|undefined, product_name: string,
 *            variant: string, unit_price_cents: number|null}}
 */
export function mapOrderItem(item) {
  const cp = (item && item.campaign_product) || {};
  const variantParts = [
    item && item.primary_attribute_value,
    item && item.secondary_attribute_value,
  ].filter((p) => p != null && `${p}`.length > 0);
  return {
    quantity: item ? item.quantity : undefined,
    product_name: cp.product_name || "Item",
    variant: variantParts.join(" / "),
    unit_price_cents: dollarsToCents(item ? item.price : null),
  };
}

/**
 * Project the fulfillment read's ship-to + shipments into the `shipping`
 * block OrderDetailView renders ({name, address1, ..., shipments}).
 *
 * Null when there is neither an address nor a shipment, so the view shows
 * its "No shipping details on file." line only when that is true. Tracking
 * survives a missing address: a box that shipped is worth listing either way.
 *
 * @param {object|null|undefined} fulfillment  the endpoint's response body
 * @returns {object|null}
 */
export function mapShipping(fulfillment) {
  if (!fulfillment || typeof fulfillment !== "object") return null;
  const ship = fulfillment.shipping || null;
  const shipments = Array.isArray(fulfillment.shipments)
    ? fulfillment.shipments
    : [];
  if (!ship && shipments.length === 0) return null;
  const s = ship || {};
  return {
    name: s.recipient_name || null,
    address1: s.address1 || null,
    address2: s.address2 || null,
    city: s.city || null,
    state: s.state || null,
    postal_code: s.postal_code || null,
    country: s.country || null,
    shipments,
  };
}

/**
 * The batch the order is in, labelled and statused the way the store's
 * Batches page shows it -- through that page's own `normalizeBatch`, so the
 * two cannot drift (a named batch shows its name, an unnamed one "B-<id>").
 *
 * @param {object|null|undefined} fulfillment  the endpoint's response body
 * @returns {{id: number, label: string, status: string, storeId: number}|null}
 *   null when the order is not in a batch
 */
export function mapOrderBatch(fulfillment) {
  const raw = fulfillment && fulfillment.batch;
  if (!raw || typeof raw !== "object") return null;
  const normalized = normalizeBatch(raw);
  return {
    id: raw.id,
    label: normalized.id,
    status: normalized.status,
    storeId: raw.store_id,
  };
}

/**
 * Project a jv-normalized order + its line items + resolved store name into the
 * CLIENT-SAFE object OrderDetailView consumes.
 *
 * @param {object} jvOrder  jsonapi-vuex normalized Order (flat, has `_jv`,
 *   `order_number`, `status`, `created_at`, `total`, `campaign_id`,
 *   `checkout_fields`, `client_department_name`)
 * @param {Array}  items    jv-normalized OrderItem list (campaign_product included)
 * @param {string|null} storeName  the store's display name (fetched separately)
 * @param {object|null} [fulfillment]  the fulfillment read's body, for
 *   `shipping`; absent (or a failed read) means no shipping block
 * @returns {object|null} client-safe order shape, or null if jvOrder is empty
 */
export function mapOrderForDetailView(jvOrder, items, storeName, fulfillment) {
  if (!jvOrder || typeof jvOrder !== "object") return null;

  const id = jvOrder._jv && jvOrder._jv.id != null ? jvOrder._jv.id : null;

  return {
    id,
    order_number: jvOrder.order_number || null,
    status: jvOrder.status || null,
    placed_at: jvOrder.created_at || null,
    shopper_name: shopperNameFromCheckoutFields(jvOrder.checkout_fields),
    store_name: storeName || null,
    // The Order column is client_department_name (there is no bare `department`).
    department: jvOrder.client_department_name || null,
    items: Array.isArray(items) ? items.map(mapOrderItem) : [],
    // What was CHARGED (the frozen charged_amount: items + tax + fees -
    // discounts), the same figure the confirmation email and the profit
    // breakdown's Payment line state. `total` is the item sum only; showing
    // it as "Total" put $7.63 beside a charged $8.48 (card #400, same
    // pattern as #396). A recorded 0 is a real total (allowance-funded);
    // only a row with no recorded charge falls back to the item sum.
    total_cents: dollarsToCents(
      jvOrder.charged_amount != null ? jvOrder.charged_amount : jvOrder.total,
    ),
    shipping: mapShipping(fulfillment),
    // Decided server-side (notifications.gating.shopper_email) -- never
    // re-derived here, so the placed-on-behalf rule lives in one place.
    shopper_email: (fulfillment && fulfillment.shopper_email) || null,
  };
}
